/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   08/11/2022
 */

import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab } from "@mui/material";
import { useForm, UseFormReturnType } from "@mantine/form";
import { Calendar, CalendarConfig } from "../interfaces/Global";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../state/store";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import CalendarSettingsGeneral from "./CalendarSettingsGeneral";
import CalendarSettingsCapacity from "./CalendarSettingsCapacity";
import CalendarSettingsDisplay from "./CalendarSettingsDisplay";
import CalendarSettingsEmployees from "./CalendarSettingsEmployees";
import { set as setCalendar } from "../state/reducers/calendar";
import { set as setSettings } from "../state/reducers/settings";
import { set as setFilter } from "../state/reducers/employees-filter";
import { toast } from "react-toastify";

export interface CalendarSettingsDialogProps {
	open: boolean;
	onClose: () => void;
	savedCalendar?: Calendar | null;
}

export type CalendarSettingsForm = Required<CalendarConfig> & { title: string };

export default function CalendarSettingsDialog({ open, onClose, savedCalendar }: CalendarSettingsDialogProps) {

	const dispatch = useDispatch();
	const calendar = useSelector((s: State) => s.calendar);
	const employeesFilter = useSelector((s: State) => s.employeesFilter);
	const settings = useSelector((s: State) => s.settings);

	const form = useForm<CalendarSettingsForm>({
		initialValues: {
			title: calendar.title,
			employeesFilter,
			settings
		},
		validate: {
			title: (v) => v.length > 0 ? null : "Calendar title cannot be blank"
		}
	});

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			scroll={"paper"}
			maxWidth={"lg"}
			PaperProps={{
				sx: {
					height: 650
				}
			}}
		>
			<DialogTitle>Calendar Settings</DialogTitle>
			<DialogContent>
				<SettingsTabs form={form} savedCalendar={savedCalendar}/>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					type={"submit"}
					variant={"contained"}
					color={"primary"}
					onClick={() => form.onSubmit(
						(values) => {
							if (form.isDirty("title")) {
								dispatch(setCalendar({ ...calendar, title: values.title }));
							}
							if (form.isDirty("employeesFilter")) {
								dispatch(setFilter(values.employeesFilter));
							}
							dispatch(setSettings(values.settings));
							onClose();
						},
						(errors) => {
							toast.error(Object.entries(errors).map(([attr, message]) => `${attr}: ${message}`).join(", "))
						}
					)()}
				>
					Update
				</Button>
			</DialogActions>
		</Dialog>
	);

}

function SettingsTabs({
	                      form,
	                      savedCalendar
                      }: { form: UseFormReturnType<CalendarSettingsForm>, savedCalendar?: Calendar | null }) {

	const [ value, setValue ] = React.useState("general");

	const handleChange = (event: React.SyntheticEvent, newValue: string) => {
		setValue(newValue);
	};

	return (
		<TabContext value={value}>
			<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
				<TabList onChange={handleChange} aria-label="lab API tabs example">
					<Tab label="General" value="general"/>
					<Tab label="Employees" value="employees-filter"/>
					<Tab label="Display" value="display"/>
					<Tab label="Capacity" value="capacity"/>
				</TabList>
			</Box>
			<TabPanel value="general">
				<CalendarSettingsGeneral
					form={form}
					savedCalendar={savedCalendar}
				/>
			</TabPanel>
			<TabPanel value="employees-filter">
				<CalendarSettingsEmployees
					form={form}
				/>
			</TabPanel>
			<TabPanel value="display">
				<CalendarSettingsDisplay
					form={form}
				/>
			</TabPanel>
			<TabPanel value="capacity">
				<CalendarSettingsCapacity
					form={form}
				/>
			</TabPanel>
		</TabContext>
	);
}